import type { Ref } from 'vue';
import type {
  DocumentItem,
  LibraryListResponse,
  LibrarySearchSnippet,
} from '@/js/account/entities/document/model/types';
import { computed, ref, watch } from 'vue';
import { DOCUMENT_LIST_PER_PAGE } from '@/js/account/entities/document/model/use-document-list-browse';
import { useQuerySync } from '@/js/account/shared/lib';

export const LIBRARY_SEARCH_SNIPPETS_PREVIEW = 3;

export type LibrarySearchViewMode = 'grouped' | 'flat';

export type LibrarySearchMatch = {
  key: string
  /** Running number across the whole result set, shown in the flat list. */
  number: number
  /** Number within its own document, shown inside a group. */
  numberInDocument: number
  document: DocumentItem
  snippet: LibrarySearchSnippet
};

export type LibrarySearchGroup = {
  document: DocumentItem
  matchesTotal: number
  truncated: boolean
  matches: LibrarySearchMatch[]
};

/**
 * Presentation state for the search results: the whole set arrives in one
 * response, so grouping, expanding and paging never touch the network.
 */
export function useLibrarySearchResults(list: Ref<LibraryListResponse | null>) {
  const query = useQuerySync();

  const mode = ref<LibrarySearchViewMode>(
    query.read('view') === 'flat' ? 'flat' : 'grouped',
  );
  const requestedPage = ref(query.readPage());
  const collapsedIds = ref<number[]>([]);
  const expandedIds = ref<number[]>([]);

  const meta = computed(() => list.value?.search ?? null);

  const groups = computed<LibrarySearchGroup[]>(() => {
    const response = list.value;

    if (!response?.search) {
      return [];
    }

    const byId = new Map(response.items.map(item => [item.id, item]));
    let running = 0;

    return response.search.results.flatMap((result) => {
      const document = byId.get(result.documentId);

      if (!document) {
        return [];
      }

      return [{
        document,
        matchesTotal: result.matchesTotal,
        truncated: result.truncated,
        matches: result.snippets.map((snippet, index) => {
          running += 1;

          return {
            key: `${result.documentId}-${index}`,
            number: running,
            numberInDocument: index + 1,
            document,
            snippet,
          };
        }),
      }];
    });
  });

  const flatMatches = computed<LibrarySearchMatch[]>(() =>
    groups.value.flatMap(group => group.matches),
  );

  const total = computed(() =>
    mode.value === 'grouped' ? groups.value.length : flatMatches.value.length,
  );

  // A restored `?page=` may outlive the result set that made it valid.
  const page = computed(() =>
    Math.min(
      requestedPage.value,
      Math.max(1, Math.ceil(total.value / DOCUMENT_LIST_PER_PAGE)),
    ),
  );

  const offset = computed(() => (page.value - 1) * DOCUMENT_LIST_PER_PAGE);

  const pagedGroups = computed(() =>
    groups.value.slice(offset.value, offset.value + DOCUMENT_LIST_PER_PAGE),
  );

  const pagedMatches = computed(() =>
    flatMatches.value.slice(offset.value, offset.value + DOCUMENT_LIST_PER_PAGE),
  );

  function isOpen(documentId: number): boolean {
    return !collapsedIds.value.includes(documentId);
  }

  function toggleGroup(documentId: number): void {
    collapsedIds.value = isOpen(documentId)
      ? [...collapsedIds.value, documentId]
      : collapsedIds.value.filter(id => id !== documentId);
  }

  function isExpanded(documentId: number): boolean {
    return expandedIds.value.includes(documentId);
  }

  function expandGroup(documentId: number): void {
    if (!isExpanded(documentId)) {
      expandedIds.value = [...expandedIds.value, documentId];
    }
  }

  function setMode(next: LibrarySearchViewMode): void {
    mode.value = next;
    requestedPage.value = 1;
    query.patch({ view: next === 'grouped' ? '' : next, page: '' });
  }

  function setPage(next: number): void {
    requestedPage.value = next;
    query.patch({ page: next === 1 ? '' : String(next) });
  }

  // A new response means a new question: keep no state from the previous one.
  watch(list, () => {
    mode.value = 'grouped';
    requestedPage.value = 1;
    collapsedIds.value = [];
    expandedIds.value = [];
    query.patch({ view: '', page: '' });
  });

  return {
    meta,
    mode,
    page,
    perPage: DOCUMENT_LIST_PER_PAGE,
    total,
    groups,
    flatMatches,
    pagedGroups,
    pagedMatches,
    isOpen,
    toggleGroup,
    isExpanded,
    expandGroup,
    setMode,
    setPage,
  };
}
